import { useState } from 'react';

import { LocalStorage } from 'saddlebag-localstorage';

import { withRtlSupport } from '@skyscanner/backpack-web/bpk-component-icon';
import ChevronLeft from '@skyscanner/backpack-web/bpk-component-icon/lg/chevron-left';
import BpkNavigationBar, {
  BpkNavigationBarIconButton,
} from '@skyscanner/backpack-web/bpk-component-navigation-bar';

import SkyscannerLogo from './SkyscannerLogo';

import type { OnSavedStatusChanged } from '@shared/types/frontend/save-to-list';
import type { I18nService } from '@shared/types/i18n';
import type { EntityId } from '@shared/types/utils';

import STYLES from './PrimaryHeader.module.scss';

import {
  useLowestPrice,
  usePriceContext,
  useStay,
  useDestinationEntityId,
} from '@hotels-hotel-detail/data-access/hooks/useHotelOffersContext';
import { SAVED_STATUS } from '@shared/constants/save-status';
import SaveButton from '@shared/ui/components/SaveButton';
import SaveHotelsFloatingNotification from '@shared/ui/components/SaveHotelsFloatingNotification';
import { withI18n } from '@shared/ui/contexts/i18n';
import { CLICK_AREA } from '@shared/utils-frontend/mini-events/constants';

const AlignedChevronIcon = withRtlSupport(ChevronLeft);
const dayviewPathnameReg = /\/.*\/search/;
const detailPagePathnameReg = /\/.*\/ht-\d+/;

const localStorage = new LocalStorage('hotelsWebsiteLocalStorage');
const historyBack = () => {
  const prevPage = window.location.href;

  window.history.back();

  // WORKAROUND: We can't directly check user can go back or not. Check the previous href to judge if it has redirected.
  setTimeout(() => {
    if (window.location.href === prevPage) {
      window.location.href = window.document.referrer;
    }
  }, 500);
};

type Props = {
  i18n: I18nService;
  backHref: string;
  id: EntityId;
};

const PrimaryHeader = ({ backHref, i18n, id }: Props) => {
  const [saveStatus, setSaveStatus] = useState(SAVED_STATUS.RESET);
  const [statusText, setStatusText] = useState<string>();

  const onSavedStatusChanged: OnSavedStatusChanged = ({
    payload: { statusText: newStatusText },
    saveStatus: newSaveStatus,
  }) => {
    setSaveStatus(newSaveStatus);
    setStatusText(newStatusText);
  };
  const backClicked = (e?: any) => {
    e.preventDefault();

    localStorage.trySetValue('backFromHD', 1);

    const isReferredFromMe = window.document.referrer.startsWith(
      window.location.origin,
    );
    if (!isReferredFromMe) {
      window.location.href = backHref;
      return;
    }
    const windowOpener = window.opener;
    const verifiedPathname =
      windowOpener &&
      (dayviewPathnameReg.test(windowOpener.location.pathname) ||
        detailPagePathnameReg.test(windowOpener.location.pathname));
    if (verifiedPathname) {
      window.close();
      return;
    }
    historyBack();
  };

  const uBffCorrelationId = usePriceContext().correlationId;
  const uBffStay = useStay();
  const uBffPrice = useLowestPrice().lowestPriceForTraveller;
  const destinationEntityId = useDestinationEntityId();

  return (
    <>
      <BpkNavigationBar
        id="primary-header"
        className={STYLES.PrimaryHeader}
        title={
          <SkyscannerLogo
            id="primary-header"
            className={STYLES.PrimaryHeader__logo}
          />
        }
        leadingButton={
          <BpkNavigationBarIconButton
            data-test-id="back-button"
            onClick={backClicked}
            icon={AlignedChevronIcon}
            label={i18n.translate('PageErrorBoundary_ErrorCard_button')}
          />
        }
        trailingButton={
          <div>
            <SaveButton
              id={id}
              stay={uBffStay}
              price={uBffPrice}
              needSplitNotification
              changeSaveStatus={onSavedStatusChanged}
              clickArea={CLICK_AREA.DETAIL_SAVE_ICON}
              correlationId={uBffCorrelationId}
              destinationEntityId={destinationEntityId}
            />
          </div>
        }
      />
      <SaveHotelsFloatingNotification
        saveStatus={saveStatus}
        statusText={statusText}
        onSavedStatusChanged={onSavedStatusChanged}
        clickArea={CLICK_AREA.DETAIL_SAVE_ICON}
        hotelId={id}
      />
    </>
  );
};

export default withI18n(PrimaryHeader);
